import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { NgFor, NgClass, NgStyle, NgIf } from '@angular/common';

Chart.register(...registerables);

@Component({
  selector: 'app-score-bar-chart',
  templateUrl: './score-bar-chart.component.html',
  styleUrls: ['./score-bar-chart.component.scss'],
  imports: [NgFor, NgClass, NgStyle, NgIf]
})
export class ScoreBarChartComponent implements OnInit {
  @ViewChild('chartCanvas', { static: true }) chartCanvas!: ElementRef<HTMLCanvasElement>;
  @Output() onGraphDataPointClick: any = new EventEmitter<any>();
  private chartInstance: Chart | any;
  @Input('chartPlugins')
  set chartPlugins(value) {
    this.yText = value?.yText;
    this.minRotation = value?.minRotation !== undefined ? value.minRotation : 90;
    this.maxRotation = value?.maxRotation !== undefined ? value.maxRotation : 90;
    this.chart = value?.chart;
    this.legend = value?.legend ?? true;
    this.stacked = (value.stacked === false) ? false : true
    this.useCustomConfig = (value?.useCustomConfig !== undefined) ? value.useCustomConfig : false;
    this.customMin = (value?.min !== undefined) ? value.min : null;
    this.customMax = (value?.max !== undefined) ? value.max : null;
    this.customStepSize = (value?.stepSize !== undefined) ? value.stepSize : null;
  }

  @Input('chartData')
  set chartData(value) {
    this.value = value;
    this.labels = value?.labels;
    this.labelLength = value?.labels ? value.labels.length : 0;
    this.dataSet = value.dataset;
    this.rating = value?.rating;
    this.ratingLabel = value?.ratingLabel;
    this.xText = value?.xText;
    this.showPercentage = value?.showPercentage;
    this.responses = value?.responses || [];
    if (this.dataSet && this.dataSet.length > 0) {
      this.legendItems = value.dataset.map((dataset: any) => ({
        label: dataset.label,
        borderColor: dataset.backgroundColor,
      }));
    }
    this.showChart();
  }

  value: any = {};
  yText: string = 'Response';
  labels: any = [];
  dataSet: any = [];
  xText: string = '';
  showPercentage: boolean = false;
  labelLength: number = 0;
  minRotation: number = 90;
  maxRotation: number = 90;
  rating: any = [];
  ratingLabel: any = '';
  responses: any[] = [];
  legendItems: any[] = [];
  chart: any = '';

  tooltipX: any = '';
  tooltipY: any = '';
  tooltipVisible: any = '';
  event: any = {};
  previousTime: any = '';
  tooltipHeading: any = '';
  tooltipData: any = '';
  legend: boolean = true;
  
  stacked: boolean = true;
  useCustomConfig: boolean = false;
  customMin: any = null;
  customMax: any = null;
  customStepSize:any = null;

  ngOnInit(): void {
    this.showChart();
  }
  ng

  private showChart(): void {
    let showPercentage = this.showPercentage;
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (ctx) {
      this.destroyChart();
      let largestValue = 0;
      for (let i = 0; i < this.labels.length; i++) {
        let totalCount = 0;
        this.dataSet.forEach(dataset => {
          totalCount = totalCount + Number(dataset?.data[i] || 0);
        });
        if (totalCount > largestValue) {
          largestValue = totalCount;
        }
      }
      largestValue = Math.ceil(largestValue / 5) * 5;
      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.labels,
          datasets: this.dataSet,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: this.stacked,
              title: {
                display: true,
                font: {
                  size: 14,
                  family: 'Lato',
                },
                text: this.xText,
                padding: {
                  top: 12,
                }
              },
              ticks: {
                maxRotation: this.maxRotation,
                minRotation: this.minRotation,
                font: {
                  size: 13,
                  family: 'Asap'
                },
                callback: function (value: any, index: number, values: any) {
                  let label: string = typeof value === 'string' ? value : String(this.getLabelForValue(value));
                  const maxCharsPerLine: number = 15;
                  const words: string[] = label.split(' ');
                  const lines: string[] = [];
                  let currentLine: string = '';
                  words.forEach((word: string) => {
                    if ((currentLine + word).length > maxCharsPerLine) {
                      lines.push(currentLine.trim());
                      currentLine = word + ' ';
                    } else {
                      currentLine += word + ' ';
                    }
                  });
                  if (currentLine) {
                    lines.push(currentLine.trim());
                  }
                  return lines;
                },
              },
            },
            y: {
              stacked: this.stacked,
              title: {
                display: true,
                font: {
                  size: 14,
                  family: 'Lato',
                },
                text: showPercentage ? '% ' + this.yText : this.yText,
              },
              ticks: {
                font: {
                  size: 13,
                  family: 'Lato'
                },
                stepSize: (this.useCustomConfig && this.customStepSize )?this.customStepSize:this.showPercentage ? (100 / 5) : (largestValue / 5),
                count: 6,
                callback: (value: string | number) => {
                  const numericValue = typeof value === 'number' ? value : Number(value);
                  if (showPercentage) {
                    return numericValue + '%';
                  } else {
                    return this.formatYAxisLabel(numericValue);
                  }
                }
              },
              min: (this.useCustomConfig && this.customMin )?this.customMin: 0,
              max: (this.useCustomConfig && this.customMax )?this.customMax:this.showPercentage ? 100 : largestValue,
            },
          },
          onClick: (events, elements) => {
            if (elements && elements.length > 0) {
              const datasetIndex = elements[0]?.index;
              this.onGraphDataPointClick.emit(this.labels[datasetIndex]);
            }
          },
          plugins: {
            tooltip: {
              enabled: false,
              intersect: false,
            },
            legend: {
              display: false,
            },
          },
        },
      });
      this.chartInstance.canvas.addEventListener('mousemove', (event) => {
        const chartArea = this.chartInstance.chartArea;
        const mouseX = event.offsetX;
        const mouseY = event.offsetY;
        const isInsideChart = mouseX >= chartArea.left && mouseX <= chartArea.right && mouseY >= chartArea.top && mouseY <= chartArea.bottom;
        if (!isInsideChart) {
          this.chartCanvas.nativeElement.style.cursor = 'default';
          this.tooltipVisible = 'none';
          return;
        }
        const intersectElements = this.chartInstance.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
        if (intersectElements.length > 0) {
          this.chartCanvas.nativeElement.style.cursor = 'pointer';
        } else {
          this.chartCanvas.nativeElement.style.cursor = 'default';
        }
        const nearestElements = this.chartInstance.getElementsAtEventForMode(event, 'nearest', { intersect: false }, false);
        if (nearestElements.length > 0) {
          const nearestIndex = nearestElements[0].index;
          if (nearestIndex >= 0 && nearestIndex < this.labels.length) {
            const visibleDataPoints = this.dataSet
              .map((dataset, datasetIndex) => {
                const meta = this.chartInstance.getDatasetMeta(datasetIndex);
                return meta.hidden ? null : {
                  label: dataset.label,
                  value: this.showPercentage ? `${dataset.data[nearestIndex]} %` : dataset.data[nearestIndex]
                };
              })
              .filter(dataPoint => dataPoint !== null);
            const obj = {
              index: nearestIndex,
              time: new Date().getTime(),
              tooltipVisible: 'block',
              label: this.labels[nearestIndex],
              response: this.responses?.[nearestIndex],
              dataPoints: visibleDataPoints
            };
            this.event = obj;
            this.tooltipVisible = 'block';
          }
        } else {
          this.tooltipVisible = 'none';
        }
      });
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const chartContainer = document.querySelector('.chart-container') as HTMLElement;
    const rect = chartContainer.getBoundingClientRect();
    if (((rect.right - rect.left) / 2) > event.offsetX) {
      this.tooltipX = event.offsetX + 20;
    } else {
      this.tooltipX = event.offsetX - 220;
    }
    this.tooltipY = event.offsetY + 15;
    if (this.previousTime !== this.event?.time && this.event?.time) {
      this.previousTime = this.event?.time;
      this.tooltipVisible = 'block';
    } else {
      this.tooltipVisible = 'none';
    }
    this.tooltipHeading = this.event?.label;
    this.tooltipData = this.event;
  }

  private destroyChart(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }
  }

  toggleDatasetVisibility(index: number) {
    const chart = this.chartInstance;
    const datasetMeta = chart.getDatasetMeta(index);
    const anyHidden = chart.data.datasets.some((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      return meta.hidden === true;
    });
    if ((datasetMeta.hidden === null || datasetMeta.hidden === false) && anyHidden) {
      chart.data.datasets.forEach((dataset, i) => {
        const meta = chart.getDatasetMeta(i);
        if (i !== index) {
          meta.hidden = false;
        }
      });
    } else {
      chart.data.datasets.forEach((dataset, i) => {
        const meta = chart.getDatasetMeta(i);
        meta.hidden = true;
      });
      datasetMeta.hidden = false;
    }
    chart.update();
  }

  isAllVisible() {
    return this.chartInstance.data.datasets.every((dataset, i) => {
      const meta = this.chartInstance.getDatasetMeta(i);
      return meta.hidden === false || meta.hidden === null;
    });
  }

  isAnyVisible() {
    return this.chartInstance.data.datasets.some((dataset, i) => {
      const meta = this.chartInstance.getDatasetMeta(i);
      return meta.hidden === false || meta.hidden === null;
    });
  }

  isSelected(index: number) {
    const meta = this.chartInstance.getDatasetMeta(index);
    return meta.hidden === false || meta.hidden === null;
  }

  isNoneSelected() {
    return !this.chartInstance.data.datasets.some((dataset, i) => {
      const meta = this.chartInstance.getDatasetMeta(i);
      return meta.hidden === true;
    });
  }

  private formatYAxisLabel(value: number): string {
    if (value >= 1e6) {
      return (value / 1e6).toFixed(1) + 'M';
    } else if (value >= 1e3) {
      return (value / 1e3).toFixed(1) + 'k';
    } else {
      return value.toString();
    }
  }
}